/**
 * Componente reutilizável para seleção de inboxes
 * 
 * Permite selecionar "Todas as inboxes" ou inboxes específicas.
 * Usado em regras de roteamento e triggers de flows.
 * 
 * @version 1.0.0
 * @date 2025-12-21
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Inbox, Globe } from 'lucide-react';
import { createLogger } from '@/lib/logger';

// Logger para o componente
const logger = createLogger('InboxSelector');

// Interface para inbox retornada do banco
interface InboxOption {
  id: string;
  name: string;
  identifier: string | null;
  channel_type: string;
}

// Props do componente
interface InboxSelectorProps {
  /** Se true, aplica a todas as inboxes (inbox_ids = null) */
  allInboxes: boolean;
  /** IDs das inboxes selecionadas (usado quando allInboxes = false) */
  selectedInboxIds: string[];
  /** Callback quando "Todas as inboxes" muda */
  onAllInboxesChange: (value: boolean) => void;
  /** Callback quando seleção individual muda */
  onSelectedIdsChange: (ids: string[]) => void;
  /** Texto opcional para o checkbox "todas" */
  allInboxesLabel?: string;
  /** Se true, mostra validação de erro quando nenhuma inbox selecionada */
  showValidation?: boolean;
}

export function InboxSelector({
  allInboxes,
  selectedInboxIds,
  onAllInboxesChange,
  onSelectedIdsChange,
  allInboxesLabel = 'Todas as inboxes',
  showValidation = false,
}: InboxSelectorProps) {
  const { currentTenant } = useAuth();

  // Buscar inboxes ativas do tenant
  const { data: inboxes, isLoading } = useQuery({
    queryKey: ['inboxes-selector', currentTenant?.id],
    queryFn: async () => {
      logger.debug('Buscando inboxes via RPC server-side', { tenantId: currentTenant?.id });

      // Usa RPC segura (não expõe provider_id, slug, instance UUIDs)
      const { data, error } = await supabase.rpc('get_tenant_inboxes_safe', {
        p_tenant_hint: currentTenant!.id,
      });

      if (error) {
        logger.error('Erro ao buscar inboxes', error);
        throw error;
      }

      // Mapeia para o formato esperado e filtra apenas inboxes ativas
      return ((data || []) as Array<any>)
        .filter((inbox) => inbox.status === 'active')
        .map((inbox) => ({
          id: inbox.id,
          name: inbox.name,
          identifier: inbox.identifier ?? null,
          channel_type: inbox.channel_type,
        })) as InboxOption[];
    },
    enabled: !!currentTenant?.id,
  });

  // Toggle de inbox individual
  const handleInboxToggle = (inboxId: string, checked: boolean) => {
    if (checked) {
      onSelectedIdsChange([...selectedInboxIds, inboxId]);
    } else {
      onSelectedIdsChange(selectedInboxIds.filter(id => id !== inboxId));
    }
  };

  // Quando marca "todas", limpa a seleção individual
  const handleAllInboxesChange = (checked: boolean) => {
    onAllInboxesChange(checked);
    if (checked) {
      onSelectedIdsChange([]);
    }
  };

  // Estado de loading
  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-5 w-32" />
        <div className="space-y-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      </div>
    );
  }

  // Exibir erro de validação
  const showError = showValidation && !allInboxes && selectedInboxIds.length === 0;

  return (
    <div className="space-y-3">
      {/* Checkbox "Todas as inboxes" */}
      <div className="flex items-center gap-2">
        <Checkbox
          id="all-inboxes"
          checked={allInboxes}
          onCheckedChange={handleAllInboxesChange}
        />
        <Label 
          htmlFor="all-inboxes" 
          className="flex items-center gap-2 cursor-pointer text-sm font-medium"
        >
          <Globe className="h-4 w-4 text-muted-foreground" />
          {allInboxesLabel}
        </Label>
      </div>

      {/* Lista de inboxes individuais */}
      {!allInboxes && (
        <div className="space-y-2 ml-6 pt-1">
          {inboxes && inboxes.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {inboxes.map((inbox) => (
                <div 
                  key={inbox.id} 
                  className="flex items-center gap-2 p-2 rounded-lg border bg-background/50 hover:bg-muted/50 transition-colors"
                >
                  <Checkbox
                    id={`inbox-${inbox.id}`}
                    checked={selectedInboxIds.includes(inbox.id)}
                    onCheckedChange={(checked) => handleInboxToggle(inbox.id, !!checked)}
                  />
                  <Label 
                    htmlFor={`inbox-${inbox.id}`} 
                    className="flex items-center gap-2 cursor-pointer text-sm flex-1"
                  >
                    <Inbox className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="truncate">{inbox.name}</span>
                    {inbox.identifier && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                        {inbox.identifier}
                      </Badge>
                    )}
                  </Label>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Nenhuma inbox ativa encontrada
            </p>
          )}

          {/* Mensagem de validação */}
          {showError && (
            <p className="text-xs text-amber-600 flex items-center gap-1">
              ⚠️ Selecione pelo menos uma inbox ou marque "{allInboxesLabel}"
            </p>
          )}
        </div>
      )}
    </div>
  );
}
